/* eslint-disable id-match */
import { faPlane } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import dayjs from 'dayjs';
import isEqual from 'lodash/isEqual';
import PropTypes from 'prop-types';
import React, { useContext, useEffect, useState } from 'react';
import {
  ALTERNATIVE_FLIGHTS,
  ALTERNATIVE_FLIGHTS_HEADING,
  NO_ALTERNATIVE_FLIGHTS,
  NO_FLIGHTS_FOR_SELECTION,
  INBOUND_RANGE_TIME,
  OUTBOUND_RANGE_TIME,
} from '../../../../config/text/text';
import {
  formatFlightsForBasket,
  getAlternativeFlightsForDate,
} from '../../../../helpers/booking';
import ThemeContext from '../../../../providers/ThemeProvider';
import WowModal from '../../../WowModal';
import Message, { MESSAGE_TYPES } from '../../../_generic/Message';
import PrimaryButton from '../../../_generic/button/PrimaryButton';
// import BookingRangeSlider from './BookingRangeSlider';
import BookingRangeSliderDynamic from './BookingRangeSliderDynamic';
import FlightPair from './FlightPair';

const BookingAlternativeFlights = ({
  date,
  departureAirport,
  inBoundDate,
  priceDiff,
  productId,
  thisDealLondonAirports,
  chooseFlight,
  selectedFlightPair,
}) => {
  const theme = useContext(ThemeContext);
  const [alternativeFlights, setAlternativeFlights] = useState(null);
  const [filteredFlights, setFilteredFlights] = useState(null);
  const [countFilteredFlights, setCountFilteredFlights] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const initialRange = [0, 24];
  const [outboundRange, setOutboundRange] = useState(initialRange);
  const [inboundRange, setInboundRange] = useState(initialRange);

  const rangeToTime = (value) => {
    if (value === 24) return '23:59';

    return `${value}:00`;
  };

  const filterFlights = () => {
    if (!alternativeFlights) return;

    // date comes DD-MM-YYYY but need YYYY-MM-DD
    const outDateToFormat = dayjs(date, 'DD-MM-YYYY', true);
    const outDateString = dayjs(outDateToFormat).format('YYYY-MM-DD');

    const outMinTime = dayjs(
      `${outDateString} ${rangeToTime(outboundRange[0])}`,
    );
    const outMaxTime = dayjs(
      `${outDateString} ${rangeToTime(outboundRange[1])}`,
    );

    const inDateString = dayjs(inBoundDate).format('YYYY-MM-DD');
    const inMinTime = dayjs(`${inDateString} ${rangeToTime(inboundRange[0])}`);
    const inMaxTime = dayjs(`${inDateString} ${rangeToTime(inboundRange[1])}`);

    const newFilteredFlights = alternativeFlights.filter((flightPair) => {
      const outTime = dayjs(flightPair.outboundJourney.departureTime);
      const inTime = dayjs(flightPair.inboundJourney.departureTime);
      const formattedFlightPair = formatFlightsForBasket(flightPair);

      if (
        outTime.isAfter(outMinTime) &&
        outTime.isBefore(outMaxTime) &&
        inTime.isAfter(inMinTime) &&
        inTime.isBefore(inMaxTime) &&
        !isEqual(formattedFlightPair, selectedFlightPair)
      ) {
        // meets conditions
        return true;
      }
    });

    setCountFilteredFlights(newFilteredFlights.length);
    setFilteredFlights(newFilteredFlights);
  };

  const onClickFlightsButton = async () => {
    setShowModal(true);
  };

  const onClickAlternativeFlight = async (flightPair) => {
    chooseFlight(flightPair);
    setShowModal(false);
  };

  const onUpdateOutboundFilter = (value) => {
    if (value[0] === outboundRange[0] && value[1] === outboundRange[1]) {
      return;
    }
    setOutboundRange(value);
  };

  const onUpdateInboundFilter = (value) => {
    if (value[0] === inboundRange[0] && value[1] === inboundRange[1]) {
      return;
    }
    setInboundRange(value);
  };

  useEffect(() => {
    if (filteredFlights) {
      setCountFilteredFlights(filteredFlights.length);
    }
  }, [filteredFlights]);

  useEffect(() => {
    filterFlights();
  }, [alternativeFlights, outboundRange, inboundRange, selectedFlightPair]);

  useEffect(() => {
    async function fetchData() {
      const altFlights = await getAlternativeFlightsForDate(
        date,
        productId,
        departureAirport,
        thisDealLondonAirports,
      );
      setAlternativeFlights(altFlights);
      setOutboundRange(initialRange);
      setInboundRange(initialRange);
    }
    if (date && productId) {
      fetchData();
    }
  }, [date, productId]);

  const noAltFlights = !alternativeFlights || alternativeFlights.length <= 1;
  const noFlightsFiltered =
    !filteredFlights || (filteredFlights.length < 1 && !noAltFlights);

  return (
    <>
      <div className="alternative-flights">
        <PrimaryButton
          aria-label={ALTERNATIVE_FLIGHTS}
          className="full-width outline"
          id="button-alternative-flights"
          onClick={onClickFlightsButton}
        >
          {ALTERNATIVE_FLIGHTS}&nbsp;
          <FontAwesomeIcon icon={faPlane} />
        </PrimaryButton>
        <WowModal
          ariaLabelledBy="button-alternative-flights"
          centered
          extraClasses=""
          setShowModal={setShowModal}
          showModal={showModal}
        >
          <div className="alternative-flights-modal">
            <h3>
              {ALTERNATIVE_FLIGHTS_HEADING} (
              <span className="alternative-flights-count">
                {countFilteredFlights}
              </span>
              &nbsp;Results)
            </h3>
            <BookingRangeSliderDynamic
              onRangeChange={onUpdateOutboundFilter}
              title={OUTBOUND_RANGE_TIME}
            />
            <BookingRangeSliderDynamic
              onRangeChange={onUpdateInboundFilter}
              title={INBOUND_RANGE_TIME}
            />
            {noAltFlights && (
              <Message
                extraClasses="message-no-background message-center message-border-radius"
                message={NO_ALTERNATIVE_FLIGHTS}
                showIcon={false}
                type={MESSAGE_TYPES.error}
              />
            )}
            {noFlightsFiltered && (
              <Message
                extraClasses="message-no-background message-center message-border-radius"
                message={NO_FLIGHTS_FOR_SELECTION}
                showIcon={false}
                type={MESSAGE_TYPES.error}
              />
            )}
            <div className="alternative-flights-list">
              {filteredFlights &&
                filteredFlights.map((flightPair, index) => (
                  <div
                    className="alternative-flight"
                    key={index}
                    onClick={() => onClickAlternativeFlight(flightPair)}
                    role="button"
                    tabIndex={0}
                  >
                    <b>{flightPair?.filterShow?.toString()}</b>
                    <FlightPair
                      flights={flightPair}
                      isAlternative
                      priceDiff={priceDiff}
                    />
                  </div>
                ))}
            </div>
          </div>
        </WowModal>
      </div>
      <style jsx>{`
        .alternative-flights {
          margin-bottom: 10px;
        }
        .alternative-flights-modal {
          margin-top: -10px;
        }
        .alternative-flights-count {
          color: ${theme.colors.primary};
        }
        .alternative-flights-list {
          height: auto;
          margin-top: 20px;
          min-height: 550px;
          padding-right: 5px;
        }
        @media (min-width: ${theme.breakpoints.smUp}) {
          .alternative-flights-list {
            height: 550px;
            overflow: auto;
          }
        }
        .alternative-flight {
          position: relative;
        }
      `}</style>
    </>
  );
};

BookingAlternativeFlights.propTypes = {
  chooseFlight: PropTypes.func.isRequired,
  date: PropTypes.string.isRequired,
  departureAirport: PropTypes.string,
  inBoundDate: PropTypes.string,
  priceDiff: PropTypes.number,
  productId: PropTypes.number.isRequired,
  selectedFlightPair: PropTypes.object.isRequired,
  thisDealLondonAirports: PropTypes.array,
};

export default BookingAlternativeFlights;
